import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiEye,
  FiArrowRight,
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import DataTable from '../../components/DataTable';
import { trustCenterApi } from '../../services/api';

const UserAssessments = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAssessments = useCallback(async () => {
    if (!user?.email) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await trustCenterApi.getSharedAssessmentsByEmail(
        user.email,
        1,
        10000
      );
      setAssessments(response.data || []);
    } catch (err) {
      console.error('Failed to fetch assessments:', err);
      setError('Failed to load assessments. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  const getRiskColor = (rating) => {
    if (rating >= 4) return 'text-[#F47F2E] font-bold';
    if (rating >= 3) return 'text-[#F47F2E]';
    if (rating >= 2) return 'text-[#53545a]';
    return 'text-[#0077b5]';
  };

  // DataTable column definitions
  const tableColumns = [
    {
      key: 'vendorName',
      header: 'Business Organization',
      sortable: true,
      filterable: true,
      render: (row) => (
        <span className="font-medium text-[#53545a]">{row.vendorName || '-'}</span>
      )
    },
    {
      key: 'accountName',
      header: 'Account Name',
      sortable: true,
      filterable: true,
      render: (row) => <span className="text-sm text-[#919191]">{row.accountName || '-'}</span>
    },
    {
      key: 'description',
      header: 'Description',
      sortable: true,
      filterable: true,
      render: (row) => {
        const desc = row.description || '';
        return <span className="text-sm text-[#919191]">{desc.length > 30 ? desc.slice(0, 30) + '...' : (desc || '-')}</span>;
      }
    },
    {
      key: 'customerOwner',
      header: 'Customer Owner',
      sortable: true,
      filterable: true,
      render: (row) => <span className="text-sm text-[#919191]">{row.customerOwner || '-'}</span>
    },
    {
      key: 'tprmOwner',
      header: 'Assessment Project',
      sortable: true,
      filterable: true,
      render: (row) => <span className="text-sm text-[#919191]">{row.tprmOwner || '-'}</span>
    },
    {
      key: 'riskRating',
      header: 'Risk Rating',
      sortable: true,
      render: (row) => (
        <span className={`font-semibold ${getRiskColor(row.riskRating)}`}>
          {row.riskRating ?? '-'}
        </span>
      )
    },
    {
      key: 'action',
      header: 'Action',
      render: (row) => (
        <button
          onClick={() => navigate(`/user/questionnaires/${row.assessmentId}`)}
          className="group relative inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#F47F2E] to-[#F47F2E]/85 text-[#ffffff] text-sm font-bold shadow-md hover:shadow-lg hover:scale-[1.04] active:scale-[0.97] transition-all duration-200 overflow-hidden"
        >
          <span className="absolute inset-0 bg-[#ffffff]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl"></span>
          <FiEye className="text-base relative z-10" />
          <span className="relative z-10">View</span>
          <FiArrowRight className="text-sm relative z-10 group-hover:translate-x-0.5 transition-transform duration-200" />
        </button>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className={`rounded-2xl p-8 bg-[#ffffff] border border-[#919191]/30 shadow-sm`}>

      {
        loading && (
          <div className="space-y-6">
            <div className='flex justify-between mb-4'> 
                   <div className="w-48 h-7 bg-[#e6eef5] rounded animate-pulse " />
                   <div className="w-64 h-7 bg-[#e6eef5] rounded animate-pulse" />
               </div>
          </div>
        )}

        {/* No Email State */}
        {!user?.email && !loading && (
          <div className="p-4 rounded-xl bg-[#919191]/5 text-center text-[#919191]">
            Unable to determine your email address. Please ensure you are logged in.
          </div>
        )}

        {user?.email && (
          <DataTable
            columns={tableColumns}
            data={assessments}
            rowKey="assessmentId"
            loading={loading}
            error={error}
            onRetry={fetchAssessments}
            searchable={true}
            searchPlaceholder="Search assessments..."
            emptyMessage="No assessments shared with you"
            totalLabel="assessments"
            rightAlignSearch={true}
            headerActions={
               <h1 className={`text-2xl font-bold text-[#53545a] mb-6`}>
          Assessments List
        </h1>
            }

          />
        )}
      </div>
    </div>
  );
};

export default UserAssessments;
